import { z } from 'zod'
import { idSchema, isoDateTimeSchema, optionalText, text } from './common.js'

const blankable = (max: number) => z.string().trim().max(max).default('')

/**
 * Free-text tags — trimmed, deduped case-insensitively, blanks dropped. Used
 * for both car brands and supply categories: the app has no fixed list for
 * either (a car's own `brand` field is free text too), so a workshop types
 * whatever it actually deals with rather than picking from a list that never
 * quite matches.
 */
const tagsSchema = z
  .array(z.string())
  .default([])
  .transform((tags) => {
    const seen = new Set<string>()
    const out: string[] = []
    for (const raw of tags) {
      const tag = raw.trim()
      if (!tag) continue
      const key = tag.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push(tag)
    }
    return out
  })

const supplierFields = {
  name: text(120),
  location: blankable(200),
  phone: blankable(32),
  /** e.g. "BMW", "Toyota" — who they stock parts for. */
  carBrands: tagsSchema,
  /** e.g. "Oil", "Brake fluid", "Coolant" — what they actually supply. */
  suppliesCategories: tagsSchema,
  offersDelivery: z.boolean().default(false),
  notes: optionalText(2000),
}

export const supplierInputSchema = z.object(supplierFields)
export type SupplierInput = z.infer<typeof supplierInputSchema>

export const supplierSchema = z.object({
  ...supplierFields,
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type Supplier = z.infer<typeof supplierSchema>
